import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';

const fechaLocal = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const hoy = () => fechaLocal(new Date());
const haceDias = (n) => { const d = new Date(); d.setDate(d.getDate() - n + 1); return fechaLocal(d); };
const formato = (f) => f ? `${f.slice(8, 10)}/${f.slice(5, 7)}/${f.slice(0, 4)}` : '-';
const num = (n) => Number(n || 0).toLocaleString('es-AR');
const entero = (v) => { if (v === '' || v === null || v === undefined) return null; const n = Number(v); return Number.isInteger(n) && n >= 0 ? n : null; };
const claveJornada = (j) => `${j.etapa_id}|${j.fecha}`;
const estiloInput = { padding: 7, borderRadius: 6, border: '1px solid #cbd5e1' };

export default function AsistenciaDiariaProceso({ procesoId, cliente = axios, endpoint = '/expedientes/ingresos/estadisticasAsistenciaDiaria', endpointDetalle = '/expedientes/ingresos/detallePresentesJornada' }) {
  const [datos, setDatos] = useState(null);
  const [error, setError] = useState('');
  const [cargando, setCargando] = useState(false);
  const [etapa, setEtapa] = useState('todas');
  const [desde, setDesde] = useState(haceDias(15));
  const [hasta, setHasta] = useState(hoy());
  // Los cambios son locales y NO se envían al backend.
  const [convocadosLocales, setConvocadosLocales] = useState({});
  const [convocadosGeneral, setConvocadosGeneral] = useState('');
  const [detalle, setDetalle] = useState(null);
  const [cargandoDetalle, setCargandoDetalle] = useState(false);
  const [errorDetalle, setErrorDetalle] = useState('');
  const [busquedaDetalle, setBusquedaDetalle] = useState('');

  const cargar = useCallback(async (signal) => {
    if (!procesoId) return;
    setCargando(true);
    setError('');
    try {
      const { data } = await cliente.post(endpoint, { proceso_id: Number(procesoId) }, { signal });
      if (!data?.ok) throw new Error(data?.error || 'No se pudo consultar la asistencia');
      setDatos(data);
    } catch (e) {
      if (e?.code === 'ERR_CANCELED' || e?.name === 'CanceledError' || signal?.aborted) return;
      setError(e?.response?.data?.error || e.message || 'Error consultando asistencia');
    } finally {
      if (!signal?.aborted) setCargando(false);
    }
  }, [cliente, endpoint, procesoId]);

  useEffect(() => {
    const controller = new AbortController();
    setDatos(null);
    setConvocadosLocales({});
    setConvocadosGeneral('');
    setEtapa('todas');
    setDetalle(null);
    cargar(controller.signal);
    return () => controller.abort();
  }, [cargar]);

  const etapas = useMemo(() => datos?.etapas || [], [datos]);
  const filas = useMemo(() => (datos?.jornadas || [])
    .filter(j => (etapa === 'todas' || String(j.etapa_id) === etapa) && (!desde || j.fecha >= desde) && (!hasta || j.fecha <= hasta))
    .map(j => {
      const matriculados = entero(j.matriculados) ?? 0;
      const presentes = entero(j.presentes) ?? 0;
      const clave = claveJornada(j);
      const local = Object.prototype.hasOwnProperty.call(convocadosLocales, clave);
      // Solo aceptamos convocados explícitos; NO asumimos que todos los matriculados debían asistir.
      const convocados = local ? entero(convocadosLocales[clave]) : (entero(convocadosGeneral) ?? entero(j.convocados));
      const validos = convocados !== null && convocados <= matriculados;
      const ausentes = validos ? Math.max(0, convocados - presentes) : null;
      const excedentes = validos ? Math.max(0, presentes - convocados) : null;
      const porcentaje = validos && convocados > 0 ? (100 * presentes / convocados) : null;
      return {
        ...j, clave, matriculados, presentes, convocados, ausentes, excedentes, porcentaje,
        convocadosInvalidos: convocados !== null && !validos,
        etapaNombre: etapas.find(e => Number(e.id) === Number(j.etapa_id))?.nombre || `Etapa ${j.etapa_id}`,
      };
    }), [datos, etapa, desde, hasta, etapas, convocadosLocales, convocadosGeneral]);

  const resumen = useMemo(() => {
    const jornadas = filas.length;
    const presentesJornada = filas.reduce((s, f) => s + f.presentes, 0);
    const conConvocatoria = filas.filter(f => f.ausentes !== null);
    const ausenciasJornada = conConvocatoria.reduce((s, f) => s + f.ausentes, 0);
    return { jornadas, presentesJornada, conConvocatoria: conConvocatoria.length, ausenciasJornada };
  }, [filas]);

  const porFecha = useMemo(() => {
    const mapa = new Map();
    filas.forEach(f => {
      const a = mapa.get(f.fecha) || { fecha: f.fecha, presentes: 0, ausentes: 0, jornadasSinConvocatoria: 0 };
      a.presentes += f.presentes;
      if (f.ausentes === null) a.jornadasSinConvocatoria += 1;
      else a.ausentes += f.ausentes;
      mapa.set(f.fecha, a);
    });
    return [...mapa.values()].sort((a, b) => a.fecha.localeCompare(b.fecha));
  }, [filas]);

  const maximo = Math.max(1, ...porFecha.map(f => f.presentes));

  const aplicarConvocadosGeneral = () => {
    if (convocadosGeneral === '' || entero(convocadosGeneral) === null) return;
    // Aplicación masiva sobre las jornadas visibles; permite modificar luego cada una.
    setConvocadosLocales(prev => {
      const nuevo = { ...prev };
      filas.forEach(f => { nuevo[f.clave] = convocadosGeneral; });
      return nuevo;
    });
  };

  const actualizarConvocados = (f, valor) => {
    if (valor !== '' && !/^\d+$/.test(valor)) return;
    setConvocadosLocales(prev => ({ ...prev, [f.clave]: valor }));
  };

  const abrirDetalle = async (jornada) => {
    setDetalle({ fecha: jornada.fecha, etapa_id: jornada.etapa_id, etapa_nombre: jornada.etapaNombre, postulantes: [] });
    setCargandoDetalle(true);
    setErrorDetalle('');
    setBusquedaDetalle('');
    try {
      const { data } = await cliente.post(endpointDetalle, {
        proceso_id: Number(procesoId), etapa_id: Number(jornada.etapa_id), fecha: jornada.fecha,
      });
      if (!data?.ok) throw new Error(data?.error || 'No se pudo obtener el detalle');
      setDetalle({ ...data, etapa_nombre: data.etapa_nombre || jornada.etapaNombre });
    } catch (e) {
      setErrorDetalle(e?.response?.data?.error || e.message || 'Error consultando presentes');
    } finally {
      setCargandoDetalle(false);
    }
  };

  const postulantesFiltrados = (detalle?.postulantes || []).filter(p =>
    `${p.dni} ${p.apellido || ''} ${p.nombre || ''}`.toLocaleLowerCase('es').includes(busquedaDetalle.toLocaleLowerCase('es'))
  );

  const exportarDetalle = () => {
    if (!detalle?.postulantes?.length) return;
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(postulantesFiltrados.map((p, i) => ({
      'N°': i + 1, DNI: p.dni, Apellido: p.apellido || '', Nombre: p.nombre || '',
      Fecha: formato(detalle.fecha), Etapa: detalle.etapa_nombre,
    })));
    XLSX.utils.book_append_sheet(wb, ws, 'Presentes');
    XLSX.writeFile(wb, `Presentes_${procesoId}_${detalle.etapa_id}_${detalle.fecha}.xlsx`);
  };

  const exportar = () => {
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(filas.map(f => ({
      Fecha: formato(f.fecha), Etapa: f.etapaNombre,
      'Matriculados activos (referencia)': f.matriculados,
      'Convocados (declarados)': f.convocadosInvalidos ? null : f.convocados,
      Presentes: f.presentes,
      'Ausentes de la jornada': f.ausentes,
      'Excedentes sobre convocados': f.excedentes,
      'Asistencia %': f.porcentaje === null ? null : Number(f.porcentaje.toFixed(2)),
      Observacion: f.convocadosInvalidos ? 'Convocados inválidos: revisar' : f.convocados === null ? 'Sin convocatoria informada' : '',
    }))), 'Asistencia por jornada');
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(porFecha.map(f => ({
      Fecha: formato(f.fecha), 'Presentes por etapa-jornada': f.presentes,
      'Ausencias por etapa-jornada (parcial)': f.ausentes,
      'Jornadas sin convocatoria válida': f.jornadasSinConvocatoria,
    }))), 'Resumen por fecha');
    XLSX.writeFile(wb, `Asistencia_diaria_proceso_${procesoId}.xlsx`);
  };

  return <section style={{ background: '#fff', border: '1px solid #dce4ee', borderRadius: 12, padding: 18, margin: '18px 0', color: '#263343' }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
      <h3 style={{ margin: 0 }}>📅 Asistencia por día</h3>
      <div style={{ display: 'flex', gap: 8 }}>
        <button className="ep-btn" onClick={() => cargar()} disabled={cargando}>↻ Actualizar</button>
        <button className="ep-btn ep-btn-primary" onClick={exportar} disabled={!filas.length}>↓ Excel diario</button>
      </div>
    </div>
    <p style={{ color: '#65748a', fontSize: 13 }}>
      La matrícula es una referencia de la etapa, no la convocatoria diaria. Podés aplicar una cantidad general y personalizar cada jornada. Si asisten más personas que las convocadas, se informa el excedente y la asistencia puede superar el 100 %.
    </p>
    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'end', margin: '14px 0' }}>
      <label style={{ display: 'grid', gap: 5, fontSize: 13 }}>Etapa
        <select value={etapa} onChange={e => setEtapa(e.target.value)} style={estiloInput}>
          <option value="todas">Todas las etapas</option>
          {etapas.map(e => <option key={e.id} value={String(e.id)}>{e.nombre}</option>)}
        </select>
      </label>
      <label style={{ display: 'grid', gap: 5, fontSize: 13 }}>Desde<input type="date" value={desde} onChange={e => setDesde(e.target.value)} style={estiloInput} /></label>
      <label style={{ display: 'grid', gap: 5, fontSize: 13 }}>Hasta<input type="date" value={hasta} onChange={e => setHasta(e.target.value)} style={estiloInput} /></label>
      {[7, 15, 30].map(n => <button key={n} className="ep-btn" onClick={() => { setDesde(haceDias(n)); setHasta(hoy()); }}>Últimos {n} días</button>)}
      <button className="ep-btn" onClick={() => { setDesde(''); setHasta(''); }}>Todo el período</button>
    </div>
    <div style={{ display: 'flex', alignItems: 'end', gap: 10, flexWrap: 'wrap', background: '#f4f7fb', padding: 12, borderRadius: 8, marginBottom: 12 }}>
      <label style={{ display: 'grid', gap: 5, fontSize: 13 }}>Convocados por defecto (jornadas visibles)
        <input type="number" min="0" step="1" value={convocadosGeneral} onChange={e => { if (e.target.value === '' || /^\d+$/.test(e.target.value)) setConvocadosGeneral(e.target.value); }} placeholder="Ej.: 200" style={{ ...estiloInput, width: 145 }} />
      </label>
      <button type="button" className="ep-btn ep-btn-primary" disabled={convocadosGeneral === '' || !filas.length} onClick={aplicarConvocadosGeneral}>Aplicar a las jornadas visibles</button>
      <button type="button" className="ep-btn" onClick={() => { setConvocadosGeneral(''); setConvocadosLocales({}); }}>Restablecer convocados</button>
      <small style={{ color: '#64748b', flex: '1 1 260px' }}>Podés cambiar cada día debajo. Los valores son locales y no se guardan en el servidor.</small>
    </div>
    {error && <div style={{ background: '#fff0f0', padding: 10, borderRadius: 6, color: '#9c2929' }}>{error}</div>}
    {cargando && <p>Consultando jornadas…</p>}
    <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', margin: '14px 0' }}>
      {[
        ['Etapa-jornadas registradas', resumen.jornadas],
        ['Registros de presencia (acumulados)', resumen.presentesJornada],
        ['Jornadas con convocatoria válida', resumen.conConvocatoria],
      ].map(([t, v]) => <div key={t} style={{ background: '#f4f7fb', borderRadius: 8, padding: 12, minWidth: 150 }}><small>{t}</small><strong style={{ display: 'block', fontSize: 23 }}>{num(v)}</strong></div>)}
    </div>
    {porFecha.length > 0 && <div style={{ overflowX: 'auto', padding: '12px 0' }}>
      <div style={{ display: 'flex', alignItems: 'end', gap: 10, minWidth: Math.max(500, porFecha.length * 54), height: 185, borderBottom: '1px solid #cbd5e1' }}>
        {porFecha.map(f => <div key={f.fecha} title={`${formato(f.fecha)} · Presencias registradas ${f.presentes}`} style={{ flex: 1, minWidth: 34, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'end', height: '100%' }}>
          <small>{f.presentes}</small>
          <div style={{ width: '75%', height: `${Math.max(2, 130 * f.presentes / maximo)}px`, background: '#21875f', borderRadius: '4px 4px 0 0' }} />
          <small style={{ fontSize: 10, whiteSpace: 'nowrap' }}>{f.fecha.slice(8, 10)}/{f.fecha.slice(5, 7)}</small>
        </div>)}
      </div>
    </div>}
    <div style={{ overflowX: 'auto' }}>
      <table className="ep-tabla">
        <thead><tr><th>Fecha</th><th>Etapa</th><th>Matriculados*</th><th>Convocados ese día</th><th>Presentes</th><th>Ausentes del día</th><th>Excedentes</th><th>Asistencia</th></tr></thead>
        <tbody>
          {filas.map((f, i) => <tr key={`${f.clave}-${i}`}>
            <td><button type="button" onClick={() => abrirDetalle(f)} title="Ver postulantes presentes" style={{ border: 0, background: 'transparent', color: '#155e9a', cursor: 'pointer', textDecoration: 'underline', fontWeight: 700 }}>{formato(f.fecha)} ↗</button></td><td>{f.etapaNombre}</td><td>{num(f.matriculados)}</td>
            <td>
              <input type="number" min="0" step="1" value={Object.prototype.hasOwnProperty.call(convocadosLocales, f.clave) ? convocadosLocales[f.clave] : (convocadosGeneral !== '' ? convocadosGeneral : (f.convocados ?? ''))} placeholder="Sin informar" onChange={e => actualizarConvocados(f, e.target.value)} style={{ ...estiloInput, width: 110, borderColor: f.convocadosInvalidos ? '#dc2626' : '#cbd5e1' }} />
              {f.convocadosInvalidos && <small style={{ display: 'block', color: '#b91c1c' }}>No puede superar la matrícula de referencia</small>}
            </td>
            <td style={{ color: '#16845b', fontWeight: 700 }}>{num(f.presentes)}</td>
            <td>{f.ausentes === null ? '—' : num(f.ausentes)}</td>
            <td style={{ color: f.excedentes > 0 ? '#b45309' : 'inherit', fontWeight: f.excedentes > 0 ? 700 : 400 }}>{f.excedentes === null ? '—' : num(f.excedentes)}</td>
            <td>{f.porcentaje === null ? '—' : `${f.porcentaje.toFixed(1).replace('.', ',')}%`}</td>
          </tr>)}
          {!filas.length && <tr><td colSpan={8}>No hay jornadas registradas para este filtro.</td></tr>}
        </tbody>
      </table>
    </div>
    {detalle && <div role="dialog" aria-modal="true" aria-label="Detalle de presentes" style={{ position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(15,23,42,.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }} onClick={() => setDetalle(null)}>
      <div onClick={e => e.stopPropagation()} style={{ background: '#fff', width: 'min(960px, 100%)', maxHeight: '90vh', overflow: 'auto', padding: 20, borderRadius: 12, boxShadow: '0 20px 60px #0003' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', gap: 12 }}>
          <div><h3 style={{ margin: 0 }}>Postulantes presentes · {formato(detalle.fecha)}</h3><p style={{ margin: '5px 0 12px', color: '#64748b' }}>{detalle.etapa_nombre}</p></div>
          <button type="button" className="ep-btn" onClick={() => setDetalle(null)}>✕ Cerrar</button>
        </div>
        {cargandoDetalle ? <p>Consultando postulantes al servidor…</p> : errorDetalle ? <p style={{ color: '#b91c1c' }}>{errorDetalle}</p> : <>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
            <strong>{num(detalle.total ?? detalle.postulantes?.length)} presentes únicos</strong>
            <input aria-label="Buscar DNI o nombre" placeholder="Buscar por DNI, apellido o nombre" value={busquedaDetalle} onChange={e => setBusquedaDetalle(e.target.value)} style={{ ...estiloInput, flex: '1 1 220px' }} />
            <button type="button" className="ep-btn ep-btn-primary" onClick={exportarDetalle} disabled={!postulantesFiltrados.length}>↓ Excel de presentes</button>
          </div>
          <div style={{ overflowX: 'auto' }}><table className="ep-tabla" style={{ width: '100%' }}><thead><tr><th>#</th><th>DNI</th><th>Apellido</th><th>Nombre</th></tr></thead><tbody>
            {postulantesFiltrados.map((p, i) => <tr key={p.dni}><td>{i + 1}</td><td>{p.dni}</td><td>{p.apellido || '—'}</td><td>{p.nombre || '—'}</td></tr>)}
            {!postulantesFiltrados.length && <tr><td colSpan={4}>No se encontraron postulantes.</td></tr>}
          </tbody></table></div>
        </>}
      </div>
    </div>}
    <small style={{ display: 'block', marginTop: 10, color: '#65748a' }}>
      * Matrícula activa informada por el backend (puede ser la matrícula actual, no la histórica de cada fecha). Las cantidades de convocados editadas aquí son temporales: no se guardan en la base de datos y se pierden al recargar. Los totales de presencia representan registros por etapa y fecha, no personas únicas. No se informa un total de personas ausentes porque sumar ausencias diarias duplicaría DNI.
    </small>
  </section>;
}
